/**
 * Where uploaded media lives, and turning a stored path into a URL.
 *
 * The API stores images as keys (`avatars/3b1e….png`) and publishes the bucket
 * they live in once, on `GET /v1/public/media`:
 *
 *   { "media_base_url": "https://algorithm-arena-dev.s3.ap-south-1.amazonaws.com/" }
 *
 * That is fetched the first time the panel loads and kept in `localStorage`,
 * so later loads do not ask again. The cache key carries the API origin, so
 * pointing a build at another environment fetches that environment's bucket
 * instead of reusing the last one's.
 *
 * `mediaUrl` leaves absolute URLs alone (`https:`, `data:`, `blob:`). Rows
 * written before the API switched to keys, and announcement images, which are
 * entered as full https links, keep working unchanged.
 */

const ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? "").trim().replace(/\/+$/, "");
const STORAGE_KEY = `aa.media_base_url:${ORIGIN}`;

let base: string | null = read();

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function write(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Blocked storage: kept in memory for this load, fetched again next time.
  }
}

/**
 * Loads the media base URL, once.
 *
 * Resolves immediately when it is already cached. Never rejects: a panel that
 * cannot reach this endpoint still works, it only shows initials where
 * pictures would have been — and the next load tries again, because only a
 * successful answer is stored.
 */
export async function initMedia(): Promise<void> {
  if (base) return;
  try {
    const response = await fetch(`${ORIGIN}/v1/public/media`);
    if (!response.ok) return;
    const body = (await response.json()) as { media_base_url?: unknown };
    if (typeof body.media_base_url === "string" && body.media_base_url.trim() !== "") {
      base = body.media_base_url.trim();
      write(base);
    }
  } catch {
    // Offline or refused; see above.
  }
}

/** A stored media path as a URL to load, or null when there is no image. */
export function mediaUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (!base) return null;
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}
